import { useState, ReactNode } from 'react';
import kit from './kit.module.css';
import styles from './DecisionExplanationDemo.module.css';

type PanelId = 'statement' | 'gap' | 'seniority';

interface Section {
  id: PanelId;
  label: string;
  content: ReactNode;
  why: string;
  actions: string[];
}

const SECTIONS: Section[] = [
  {
    id: 'statement',
    label: 'PERSONAL STATEMENT',
    content: (
      <p className={styles.statement}>
        A dedicated and empathetic care professional with over four years of experience supporting
        adults in community settings. Known for building trust quickly and staying calm under
        pressure. Seeking a mid-level role where strong interpersonal skills can make a real difference.
      </p>
    ),
    why: "You didn't specify a preferred tone, so I used a warm, professional style - common for care sector roles based on the job description.",
    actions: ['Change tone', 'Keep it'],
  },
  {
    id: 'gap',
    label: 'EMPLOYMENT NOTE',
    content: <span className={kit.chip}>Career break 2021-2022</span>,
    why: 'I found a gap between Jan 2021 and Sep 2022. Rather than leave it blank, I added a neutral note - employers typically notice unexplained gaps.',
    actions: ['Edit this note', 'Remove it', 'Keep it'],
  },
  {
    id: 'seniority',
    label: 'SENIORITY',
    content: <span className={kit.chip}>Mid-level candidate</span>,
    why: 'Based on 4 years of total experience and no management roles listed, I estimated mid-level. This affects how the CV is structured and worded.',
    actions: ['Change level', 'Keep it'],
  },
];

export function DecisionExplanationDemo() {
  const [openPanel, setOpenPanel] = useState<PanelId | null>(null);
  const [feedback, setFeedback] = useState('Tap "Why this?" on any section to see the reasoning behind it.');

  function toggle(id: PanelId) {
    setOpenPanel((prev) => (prev === id ? null : id));
  }

  function choose(section: Section, action: string) {
    setOpenPanel(null);
    setFeedback(`${action} - ${section.label.toLowerCase()}. In a real product this would update the CV.`);
  }

  return (
    <div className={kit.root}>
      <div className={kit.message}>Generate Sarah's CV for care coordinator roles.</div>
      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✦</span>
        <p>Here's the draft. I made a few calls on your behalf - ask me about any of them.</p>
      </div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Generated CV</span>
          <span>3 decisions</span>
        </div>
        <h3 className={kit.proposalTitle}>Sarah Mitchell</h3>

        <div className={kit.stack}>
          {SECTIONS.map((section, i) => {
            const isOpen = openPanel === section.id;
            return (
              <div key={section.id} className={kit.stack}>
                {i > 0 && <hr className={kit.divider} />}
                <div className={styles.sectionHead}>
                  <span className={kit.label}>{section.label}</span>
                  <button
                    type="button"
                    className={`${kit.btn} ${kit.btnGhost} ${styles.why} ${isOpen ? styles.whyOpen : ''}`}
                    onClick={() => toggle(section.id)}
                    aria-expanded={isOpen}
                  >
                    Why this?
                  </button>
                </div>
                <div>{section.content}</div>
                {isOpen && (
                  <div className={styles.explain}>
                    <div className={kit.agent}>
                      <span className={kit.agentIcon} aria-hidden="true">✦</span>
                      <p>{section.why}</p>
                    </div>
                    <div className={kit.row}>
                      {section.actions.map((label, j) => (
                        <button
                          key={label}
                          type="button"
                          className={`${kit.btn} ${j === section.actions.length - 1 ? kit.btnApprove : kit.btnEdit}`}
                          onClick={() => choose(section, label)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
